import { Injectable, Injector } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ListData } from '@fi-app/components/items-list/list-data.service';
import { ILightWeightObjectCollection } from '@fi-shared/models';
import { AccessRightFilterCollectionModel } from '@fi-shared/models/access-right-filter-collection.model';
import {
    IForm,
    IFormCondition,
    IFormEdit,
    IFormQuestionPresetOption,
    IFormReport,
    ILightWeightFormAnswerSet,
} from '@fi-shared/fi-form-shared/fi-form-shared';
import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';
import { FiImageSize, FileType, PersonalNotificationsCategoryType } from '@fi-shared/types';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { AppTextService } from '@fi-shared/app-text';
import { IFormReportQuestionAnswer, IFormReportView } from '@fi-app/fi-form/fi-form-report/fi-form-report';
import { FormReportQuestionCommonListData } from '@fi-app/fi-form/fi-form-report-common-question-data';
import { FormReportQuestionAttachmentListData } from '@fi-app/fi-form/fi-form-report-attachment-question-data';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { IFormListParameter } from '@fi-app/fi-form/fi-form';
import { IItemsListDataFetchResult, IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';

@Injectable()
export class FiFormService {
    currentListData$: BehaviorSubject<ListData<any>> = new BehaviorSubject<ListData<any>>(null);
    private _newItemsCount$ = new BehaviorSubject<number>(0);
    get newItemsCount$(): Observable<number> {
        return this._newItemsCount$.asObservable();
    }

    newAnswerSets$ = new BehaviorSubject<ILightWeightFormAnswerSet[]>([]);

    constructor(
        private fiBackend: FiBackendService,
        private formHelperService: FiFormHelperService,
        private fiSignalRService: FiSignalRService,
        private fiSignalRHelperService: FiSignalRHelperService,
        private fiPrintDownloadService: FiPrintDownloadService,
        private appTextService: AppTextService,
        private loadingService: LoadingService,
    ) {
        this.fiSignalRHelperService.newFormIds$.subscribe(() => this.updateNewItemsCount());
        //this.newAnswerSets$.subscribe(() => this.updateNewItemsCount());

        this.fiSignalRService.formViewerSavedEvent.subscribe((data) => {
            const formAnswerSets = this.newAnswerSets$.value;
            formAnswerSets.push({
                Id: data.AnswerSetId,
                FormId: data.FormId,
            });
            this.newAnswerSets$.next(formAnswerSets);
        });

        this.fiSignalRHelperService.initialNewFormAnswerSets.subscribe((formAnswerSets) => {
            this.newAnswerSets$.next(formAnswerSets);
        });
    }

    private updateNewItemsCount() {
        this._newItemsCount$.next(this.fiSignalRHelperService.newFormIds$.value.length);
    }

    isFormReportUnseen(formAnswerSetIds: number[]) {
        return this.newAnswerSets$.value.some((newAnswerSet) => formAnswerSetIds.indexOf(newAnswerSet.Id) !== -1);
    }

    getUnseenFormReportAnswersCount(formAnswerSetIds: number[]) {
        return this.newAnswerSets$.value.filter((newAnswerSet) => formAnswerSetIds.indexOf(newAnswerSet.Id) !== -1)
            .length;
    }

    getUnseenFormReportAnswersByFormId(formId: number) {
        return this.newAnswerSets$.value
            .filter((newAnswerSet) => newAnswerSet.FormId === formId)
            .map((newAnswerSet) => newAnswerSet.Id);
    }

    onFormAnswerSetsAsVisited(answerSetIds: any[]) {
        const newAnswerSets = this.newAnswerSets$.value.filter((item) => answerSetIds.indexOf(item.Id) === -1);
        this.newAnswerSets$.next(newAnswerSets);
    }

    getFormsListData(parameter: IFormListParameter) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IForm>>('Form/Form/GetFormsListData', parameter);
    }

    getForms(parameterApiModel: IFormListParameter) {
        return this.fiBackend.fetchDataPost<IForm[]>('Form/Form/GetForms', parameterApiModel);
    }

    getFormsCount(parameterApiModel: IFormListParameter) {
        return this.fiBackend.fetchDataPost<number>('Form/Form/GetFormsCount', parameterApiModel);
    }

    getForm(id: number) {
        const promise = this.fiBackend.fetchData<IFormEdit>(`Form/Form/GetForm/${id}`);
        /*promise.then(form => form.Questions.forEach(question => {
            question.uid = this.formHelperService.getUniqueId();
            question.PresetOptions.forEach(presetOption => presetOption.uid = this.formHelperService.getUniqueId());
        }));*/
        return this.loadingService.trackPromise(promise);
    }

    getFormCopy(id: number) {
        const promise = new Promise<IFormEdit>((resolve, reject) => {
            this.fiBackend.fetchData<IFormEdit>(`Form/Form/GetFormCopy/${id}`).then((form) => {
                const copyInstance = this.getNewFormInstance();
                copyInstance.Name = this.getCopyOf(form.Name);
                copyInstance.Description = this.getCopyOf(form.Description);
                form.Names.forEach((item) => (item.Value = this.getCopyOf(item.Value)));
                form.Descriptions.forEach((item) => (item.Value = this.getCopyOf(item.Value)));
                copyInstance.Names = form.Names;
                copyInstance.Descriptions = form.Descriptions;
                copyInstance.Questions = form.Questions;
                copyInstance.Conditions = form.Conditions;
                resolve(copyInstance);
            }, reject);
        });
        return this.loadingService.trackPromise(promise);
    }

    deleteForms(formIds: number[]) {
        return this.loadingService.trackPromise(this.fiBackend.fetchDataPost<void>('Form/Form/DeleteForms', formIds));
    }

    restoreForms(formIds: number[]) {
        return this.loadingService.trackPromise(this.fiBackend.fetchDataPost<void>('Form/Form/RestoreForms', formIds));
    }

    getNewFormInstance(): IFormEdit {
        return {
            Names: [],
            Descriptions: [],
            AccessRightFilterCollection: AccessRightFilterCollectionModel.getNewInstance(),
            Questions: [],
            Conditions: [],
            UserIdsToSendSubmissions: [],
            IsNews: true,
            IsSendEmailOnSubmitToParticipant: true,
            NotificationCategory: PersonalNotificationsCategoryType.Visited,
            SendPushMessages: true,
        };
    }

    getNewOptionInstance(): IFormQuestionPresetOption {
        return {
            Names: [],
            uid: this.formHelperService.getUniqueId(),
        };
    }

    getNewConditionInstance(): IFormCondition {
        return {
            SourceQuestionId: undefined,
            ConditionType: undefined,
            ConditionPresetOptionIds: undefined,
            ConditionOperator: undefined,
            //ConditionPresetOptionIndexes: [],
            presetOptionUids: [],
            ChildItems: [],
            Actions: [
                {
                    Action: undefined,
                    targetQuestionUid: undefined, // to establish correct relationships
                },
            ],
        };
    }

    getLightWeightObjectsForLocalSearch(parameterApiModel) {
        return this.fiBackend.fetchDataPost<ILightWeightObjectCollection>(
            'Form/Form/GetLightWeightObjectsForLocalSearch',
            parameterApiModel,
        );
    }

    saveForm(form: IFormEdit) {
        return this.fiBackend.fetchDataPost('Form/Form/SaveForm', form);
    }

    getFormReportsListData(parameter: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IFormReport>>(
            'Form/FormReport/GetFormReportsListData',
            parameter,
        );
    }

    getFormReports(parameterApiModel: IFormListParameter) {
        return this.fiBackend.fetchDataPost<IForm[]>('Form/FormReport/GetFormReports', parameterApiModel);
    }

    getFormReportsCount(parameterApiModel) {
        return this.fiBackend.fetchDataPost<number>('Form/FormReport/GetFormReportsCount', parameterApiModel);
    }

    getLightWeightObjectsForLocalSearchFormReports(parameterApiModel) {
        return this.fiBackend.fetchDataPost<ILightWeightObjectCollection>(
            'Form/FormReport/GetLightWeightObjectsForLocalSearch',
            parameterApiModel,
        );
    }

    getFormReportCommonQuestionListDataInstance(
        injector: Injector,
        reportItemClickCallback: (item: IFormReportQuestionAnswer) => void,
    ) {
        return new FormReportQuestionCommonListData(injector, reportItemClickCallback);
    }

    getFormReportAttachmentQuestionListDataInstance(
        injector: Injector,
        reportItemClickCallback: (item: IFormReportQuestionAnswer) => void,
    ) {
        return new FormReportQuestionAttachmentListData(injector, reportItemClickCallback);
    }

    getFormReport(formId: number, startDate?: string, endDate?: string) {
        return this.fiBackend.fetchData<IFormReportView>(
            `Form/FormReport/GetFormReport/${formId}?startDate=${startDate || ''}&endDate=${endDate || ''}`,
        );
    }

    downloadReportExcel(formId: number, formName: string, startDate: string, endDate: string) {
        const url = `Form/FormReport/GetFormReportExcel/${formId}?startDate=${startDate}&endDate=${endDate}`;
        const fileName = this.fiPrintDownloadService.getReportFileName(formName, FileType.Excel);

        const promise = this.fiPrintDownloadService.downloadBinaryFile(url, {
            fileName: fileName,
            size: FiImageSize.Original,
            getMethod: true,
        });
        return promise;
    }

    private getCopyOf(value) {
        return value ? this.appTextService.get('copy_of') + ' ' + value : value;
    }

    private _reloadFormsList$ = new Subject<void>();
    doReloadFormsList$() {
        this._reloadFormsList$.next();
    }

    get reloadFormsList$(): Observable<any> {
        return this._reloadFormsList$.asObservable();
    }
}
